import { useToken } from '@ori/auth';
import { useServiceUrls } from '@ori/configuration-manager';
import { useEnvironment } from '@ori/environment';
import type { Translations } from '@ori/i18n';
import { TranslationsNamespaceProvider, TranslationsProvider, useTranslationsWithNamespaces } from '@ori/i18n';
import type { StaticApplicationsAdvisorNovAgeSkincareConfigurationDto } from '@ori/static-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  defaultTranslations,
  OPEN_HREF_ATTRIBUTE,
  OPEN_QUERY_PARAM_ALWAYS,
  OPEN_QUERY_PARAM_ONCE,
  translationsNamespace,
} from '../constants';
import { useGetNovageSkincareConfiguration, useOnHrefClick } from '../hooks';
import { mainLogger } from '../logger';
import { getValidOpenQueryParam, setOpenQueryParam as setOpenQueryParamUtil } from '../utils';

const localLogger = mainLogger.createChild('NovageSkincareAdvisorProvider');
const onError = (error: unknown) =>
  localLogger.error(
    new Error('TranslationsProvider fetching failed', {
      cause: error,
    }),
  );

export interface NovageSkincareAdvisorContext {
  initialOpenDone: boolean;
  novageSkincareConfiguration: StaticApplicationsAdvisorNovAgeSkincareConfigurationDto | undefined;
  openQueryParam: string | undefined;
}

const defaultNovageSkincareAdvisorContextValue: NovageSkincareAdvisorContext = {
  initialOpenDone: false,
  novageSkincareConfiguration: undefined,
  openQueryParam: undefined,
};

export const novageSkincareAdvisorContext = createContext<NovageSkincareAdvisorContext>(
  defaultNovageSkincareAdvisorContextValue,
);

export const { Provider } = novageSkincareAdvisorContext;

export type NovageSkincareAdvisorProviderProps = PropsWithChildren<{
  /** Translations for NovageSkincareAdvisor. */
  translations?: Translations | undefined;
}>;

export const NovageSkincareAdvisorProvider: FC<NovageSkincareAdvisorProviderProps> = ({ children, translations }) => {
  const { tenant } = useEnvironment();
  const { language } = useTranslationsWithNamespaces();
  const { staticApiUrl } = useServiceUrls(['staticApi']);
  const { token } = useToken();
  const [initialOpenDone, setInitialOpenDone] = useState(false);
  const [openQueryParam, setOpenQueryParam] = useState<string>();
  const open = useCallback(() => {
    if (openQueryParam !== OPEN_QUERY_PARAM_ALWAYS) {
      setOpenQueryParamUtil();
      setOpenQueryParam(OPEN_QUERY_PARAM_ONCE);
      setInitialOpenDone(true);
    }
  }, [openQueryParam]);
  useOnHrefClick({ href: OPEN_HREF_ATTRIBUTE, onClick: open });
  useEffect(() => {
    const initialOpenQueryParam = getValidOpenQueryParam();
    if (initialOpenQueryParam) {
      setOpenQueryParam(initialOpenQueryParam);
      setInitialOpenDone(true);
    }
  }, []);
  const novageSkincareConfiguration = useGetNovageSkincareConfiguration(initialOpenDone);

  const value = useMemo<NovageSkincareAdvisorContext>(
    () => ({ initialOpenDone, novageSkincareConfiguration, openQueryParam }),
    [initialOpenDone, novageSkincareConfiguration, openQueryParam],
  );

  return (
    <TranslationsProvider
      language={language}
      defaultTranslationsWithNamespaces={{ [translationsNamespace]: defaultTranslations }}
      translationsWithNamespaces={translations ? { [translationsNamespace]: translations } : undefined}
      clientFetchConfiguration={initialOpenDone ? { tenant, token, baseUrl: staticApiUrl, onError } : undefined}
    >
      <TranslationsNamespaceProvider namespace={translationsNamespace}>
        <Provider value={value}>{children}</Provider>
      </TranslationsNamespaceProvider>
    </TranslationsProvider>
  );
};

export const useNovageSkincareAdvisor = () => useContext(novageSkincareAdvisorContext);
